import { useEffect, useState } from "react";
import { getApiUrl } from "@/lib/config";

/**
 * GET /api/v1/website-settings (public): what the admin panel controls on the site. See docs/website-settings-contract.md.
 * One request per page load, shared by every component; the last answer is kept in localStorage so repeat visits don't flash.
 */
export type ContactInfo = {
  email: string;
  phone: string;
  whatsApp: string;
  whatsAppDigits: string;
  address: string;
  privacyEmail: string;
  legalEmail: string;
  supportEmail: string;
};

export type WebsiteSettings = {
  showFreeSignup?: boolean;
  heroVariant?: string;
  copy?: Partial<Record<"pt" | "en", Record<string, string>>>;
  contact?: Partial<ContactInfo>;
};

/** Same defaults as the API (ContactSettings.cs), used before the first answer and when the API is down. */
export const DEFAULT_CONTACT: ContactInfo = {
  email: "contato@fillsign.com.br",
  phone: "",
  whatsApp: "",
  whatsAppDigits: "",
  address: "Belo Horizonte, MG - Brasil",
  privacyEmail: "contato@fillsign.com.br",
  legalEmail: "contato@fillsign.com.br",
  supportEmail: "contato@fillsign.com.br",
};

const CACHE = "fs.websiteSettings";

export function cachedWebsiteSettings(): WebsiteSettings {
  try { return JSON.parse(localStorage.getItem(CACHE) || "{}") as WebsiteSettings; } catch { return {}; }
}

let inflight: Promise<WebsiteSettings | null> | null = null;

export function loadWebsiteSettings(): Promise<WebsiteSettings | null> {
  if (!inflight) {
    inflight = fetch(getApiUrl("/api/v1/website-settings"))
      .then((r) => (r.ok ? (r.json() as Promise<WebsiteSettings>) : null))
      .then((d) => {
        if (d) { try { localStorage.setItem(CACHE, JSON.stringify(d)); } catch { /* storage may be unavailable */ } }
        return d;
      })
      .catch(() => null);
  }
  return inflight;
}

export function useWebsiteSettings(): WebsiteSettings {
  const [settings, setSettings] = useState<WebsiteSettings>(cachedWebsiteSettings);
  useEffect(() => {
    let alive = true;
    loadWebsiteSettings().then((d) => { if (alive && d) setSettings(d); });
    return () => { alive = false; };
  }, []);
  return settings;
}

/** Contact data with defaults filled in; blank phone or WhatsApp means "don't show". */
export function useContact(): ContactInfo {
  const c = useWebsiteSettings().contact ?? {};
  const pick = (v: string | undefined, fallback: string) => (typeof v === "string" ? v.trim() : fallback);
  const email = pick(c.email, DEFAULT_CONTACT.email) || DEFAULT_CONTACT.email;
  return {
    email,
    phone: pick(c.phone, ""),
    whatsApp: pick(c.whatsApp, ""),
    whatsAppDigits: pick(c.whatsAppDigits, "").replace(/\D/g, ""),
    address: pick(c.address, DEFAULT_CONTACT.address),
    privacyEmail: pick(c.privacyEmail, "") || email,
    legalEmail: pick(c.legalEmail, "") || email,
    supportEmail: pick(c.supportEmail, "") || email,
  };
}

/** A clickable e-mail address for the legal pages. */
export function MailLink({ email }: { email: string }) {
  return <a href={`mailto:${email}`} className="underline underline-offset-2">{email}</a>;
}
